/**
 * Progetti sul computer, ridisegnati il 26/10/2026 (os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: quanto ci resta da ogni lavoro e a che punto è? Lorenzo: "in ogni
 * progetto il goal è capire quanto margine resta" (spese, IVA, tutte le tasse,
 * provvigioni degli agenti). Quindi:
 *  - in cima la risposta in una frase (quanti lavori in corso, quanto valgono,
 *    quanto ci resta dopo spese, provvigioni e tasse);
 *  - una scheda per progetto: cliente, lavoro, quanto resta, la barra dei soldi
 *    (spese, provvigione, tasse, quello che resta), avanzamento, incassato,
 *    consegna e agente;
 *  - in corso, chiusi o tutti; la ricerca. La bacheca a colonne e le consegne
 *    di Design non servivano con una manciata di progetti: tolte da qui.
 * Chi non vede i margini vede il valore del lavoro al posto del conto.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { Chip, statusTone } from '../../components/ui/Chip';
import { BrandLogo } from '../../components/ui/BrandLogo';
import { SectionMark } from '../../components/ui/SectionMark';
import { EmptyState } from '../../components/ui/EmptyState';
import { useUrlState } from '../../hooks/useUrlState';
import { leggibile } from '../../components/mobile/MobileKit';
import { fmtEur } from '../../lib/utils';
import type { Division } from '@masamune/shared';
import { STATO_PROGETTO } from './ProgettiMobile';
import { VUOTI, canWriteProjects, projectsBoardQuery, type ContoBreve, type ProjectRow } from './projectsApi';
import './progetti.css';

type Quali = 'in-corso' | 'chiusi' | 'tutti';
const euro = (n: number) => `€ ${fmtEur(n)}`;

export function ProgettiDesktop({ initialDivision }: { initialDivision?: Division }) {
  const navigate = useNavigate();
  const openModal = useModalStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const vedeMargini = !!user?.role.canSeeMargin;
  const [quali, setQuali] = useUrlState<Quali>('quali', 'in-corso');
  const [cerca, setCerca] = useState('');

  const q = useQuery(projectsBoardQuery({ ...VUOTI, division: initialDivision ?? '' }));
  const tutti = q.data?.items ?? [];
  const vivi = tutti.filter((p) => p.status !== 'CHIUSO');
  const righe = useMemo(() => {
    const base = quali === 'in-corso' ? vivi : quali === 'chiusi' ? tutti.filter((p) => p.status === 'CHIUSO') : tutti;
    const t = cerca.trim().toLowerCase();
    if (!t) return base;
    return base.filter((p) => `${p.name} ${p.code} ${p.client.shortName} ${p.client.legalName} ${p.agente?.nome ?? ''}`.toLowerCase().includes(t));
  }, [tutti, vivi, quali, cerca]);

  // La risposta in cima, sui progetti in corso.
  const valore = vivi.reduce((s, p) => s + (p.conto?.imponibile ?? p.revenue), 0);
  const resta = vivi.reduce((s, p) => s + (p.conto?.resta ?? 0), 0);
  const senzaSpese = vivi.filter((p) => p.conto && p.conto.spese === 0 && p.conto.imponibile > 0).length;
  const nuovo = () => openModal('newProject', initialDivision ? { division: initialDivision } : undefined);

  return (
    <div className="page pg-page">
      <header className="pg-hd">
        <div className="pg-hd-main">
          <SectionMark icon="cube" className="pg-sez" />
          <div className="min-w-0">
            <h1 className="pg-title">Progetti</h1>
            {q.data ? (
              <p className="pg-risposta">
                {vivi.length === 0 ? <>Nessun lavoro in corso.</> : (
                  <>
                    <strong>{vivi.length} {vivi.length === 1 ? 'lavoro in corso' : 'lavori in corso'}</strong> per <strong>{euro(valore)}</strong> senza IVA
                    {vedeMargini && <>: dopo spese, provvigioni e tasse ci restano <strong>{euro(resta)}</strong></>}.
                    {vedeMargini && senzaSpese > 0 && <> In {senzaSpese === 1 ? 'uno' : senzaSpese} mancano ancora le spese, quindi il conto vero è più basso.</>}
                  </>
                )}
              </p>
            ) : <p className="pg-risposta mute">Sto leggendo i progetti…</p>}
          </div>
        </div>
        {canWriteProjects(user) && (
          <button className="btn btn-primary" onClick={nuovo}><Icon name="plus" /> Nuovo progetto</button>
        )}
      </header>

      <div className="pg-barra">
        <div className="pg-seg" role="tablist" aria-label="Quali progetti">
          {([['in-corso', 'In corso', vivi.length], ['chiusi', 'Chiusi', tutti.length - vivi.length], ['tutti', 'Tutti', tutti.length]] as const).map(([k, l, n]) => (
            <button key={k} type="button" role="tab" aria-selected={quali === k} className="pg-seg-btn" onClick={() => setQuali(k)}>
              {l} <span className="c">{n}</span>
            </button>
          ))}
        </div>
        <div className="tb-search pg-cerca">
          <Icon name="search" size={12} />
          <input aria-label="Cerca fra i progetti" placeholder="Cerca per cliente, lavoro o agente…" value={cerca} onChange={(e) => setCerca(e.target.value)} />
        </div>
      </div>

      {q.isLoading && !q.data && <p className="pg-vuoto">Carico i progetti…</p>}
      {q.data && righe.length === 0 && (
        <EmptyState brand title={cerca ? 'Nessun progetto trovato' : quali === 'chiusi' ? 'Nessun progetto chiuso' : 'Nessun lavoro in corso'} description={cerca ? 'Prova con un altro nome.' : 'I progetti nascono quando il cliente dice di sì: da una trattativa vinta o da qui.'} />
      )}

      <div className="pg-griglia">
        {righe.map((p) => <SchedaProgetto key={p.id} p={p} vedeMargini={vedeMargini} onApri={() => navigate(`/projects/${p.id}`)} />)}
      </div>

      {vedeMargini && righe.length > 0 && (
        <p className="pg-nota">
          <Icon name="help" size={12} /> Quanto resta = valore del lavoro senza IVA, meno le spese del progetto, meno la provvigione dell'agente,
          meno IRES (24%) e IRAP (3,9%) sull'utile. L'IVA il cliente la paga a noi ma va allo Stato, quindi non conta. Il conto completo è dentro ogni progetto.
        </p>
      )}
    </div>
  );
}

/** "Villa Fago — Digital Marketing 2026" → "Digital Marketing 2026": il cliente c'è già sopra. */
export function nomeLavoro(nome: string): string {
  const i = nome.indexOf(' — ');
  return i > 0 ? nome.slice(i + 3) : nome;
}

function SchedaProgetto({ p, vedeMargini, onApri }: { p: ProjectRow; vedeMargini: boolean; onApri: () => void }) {
  const cliente = leggibile(p.client.shortName || p.client.legalName);
  const c = p.conto ?? null;
  return (
    <button type="button" className={`pg-card${p.status === 'CHIUSO' ? ' is-chiuso' : ''}`} onClick={onApri}>
      <div className="pg-card-hd">
        <BrandLogo name={cliente} size={38} />
        <div className="pg-card-chi">
          <span className="cl">{cliente}</span>
          <span className="nm">{nomeLavoro(p.name)}</span>
        </div>
        <Chip tone={statusTone(p.status)} dot>{STATO_PROGETTO[p.status] ?? p.status}</Chip>
      </div>

      {vedeMargini && c ? (
        <>
          <div className="pg-resta">
            <span className="l">Ci restano</span>
            <span className={`v${c.resta < 0 ? ' neg' : ''}`}>{euro(c.resta)}</span>
            <span className="p">{c.imponibile > 0 ? `${String(c.restaPct).replace('.', ',')}% di ${euro(c.imponibile)}` : 'manca il valore del lavoro'}</span>
          </div>
          <BarraSoldi c={c} />
        </>
      ) : (
        <div className="pg-resta"><span className="l">Valore del lavoro</span><span className="v">{euro(p.revenue)}</span><span className="p">senza IVA</span></div>
      )}

      <div className="pg-card-ft">
        <span className="pg-avanz" title="A che punto è il lavoro">
          <span className="pg-avanz-bar"><i style={{ width: `${Math.max(0, Math.min(100, p.progress))}%` }} /></span>
          {p.progress}% fatto
        </span>
        {vedeMargini && c && c.fatturato > 0 && <span>incassati {euro(c.incassato)}</span>}
        {p.deadline && <span className={p.isLate ? 'tardi' : ''}><Icon name="calendar" size={11} /> {p.isLate ? `in ritardo di ${p.giorniRitardo} g` : `consegna ${new Date(p.deadline).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}`}</span>}
        {p.agente && <span className="pg-agente"><Icon name="handshake" size={11} /> {p.agente.nome}</span>}
      </div>
    </button>
  );
}

/** La barra dei soldi: dove va l'imponibile. Monocroma: il nero pieno è quello che resta. */
function BarraSoldi({ c }: { c: ContoBreve }) {
  const tot = Math.max(1, c.imponibile);
  const pz = (x: number) => `${Math.max(0, (x / tot) * 100)}%`;
  const resta = Math.max(0, c.resta);
  return (
    <div className="pg-soldi">
      <div className="pg-soldi-bar" aria-hidden>
        <i className="resta" style={{ width: pz(resta) }} />
        <i className="tasse" style={{ width: pz(c.tasse) }} />
        <i className="provv" style={{ width: pz(c.provvigione) }} />
        <i className="spese" style={{ width: pz(c.spese) }} />
      </div>
      <div className="pg-soldi-leg">
        <span><i className="spese" />spese {euro(c.spese)}</span>
        <span><i className="provv" />provvigione {euro(c.provvigione)}</span>
        <span><i className="tasse" />tasse {euro(c.tasse)}</span>
      </div>
    </div>
  );
}
